import { FormPatientDetails } from "@/components/common/PatientDetails";
import { crfReadOnly } from "@/utils/CrfFormUtils";
import { Box, Typography } from "@mui/material";
import { ConditionMode, useFieldActions, useFieldState, useFormActions, ValidateOn } from "@ngt/forms-core";
import { Checkbox, CollaboratingGroupContext, CrfCondition, CrfForm, CtcaeSelector, CtcaeSelectorType, DatePicker, FormBreadcrumbs, FormOptionsContext, getParentPropertyPath, GroupedTableArrayField, ICrfConditionParameters, IGroupedTableArrayFieldColumn, Input, InstitutionContext, MasterGroupContext, PageLayout, PatientContext, RouteLoading, Select } from "@ngt/opms";
import { Permission, usePermissionsByIds } from "@ngt/opms-bctapi";
import { FC, useContext, useEffect, useMemo } from "react";
import { AdverseEventsForm as AdverseEventsFormDto } from "../../../api/dtos";

interface IAdverseEventFormProps {

}

export const OngoingUpdater: FC = () => {
    const { readOnly } = useContext(FormOptionsContext);
    const { value: ongoing } = useFieldState();
    const { getPropertyPath } = useFieldActions();
    
    const parentPropertyPath = useMemo(() => {
        return getParentPropertyPath(getPropertyPath());
    }, [getPropertyPath]);

    const {setFieldValue} = useFormActions();

    useEffect(() => {
        if (ongoing && !readOnly) {
            setFieldValue(`${parentPropertyPath}.resolutionDate`, undefined, true, true, true, false)
        }
    }, [ongoing, parentPropertyPath, readOnly])

    return null;
}

const resolutionOngoing = (index: number) => ({ formState }: ICrfConditionParameters<AdverseEventsFormDto>) => { 
    const ongoing = formState?.value?.adverseEventForms?.[index]?.ongoing;

    return !ongoing;
};


const AdverseEventColumns: Array<IGroupedTableArrayFieldColumn<AdverseEventsFormDto>> = [
    {
        propertyPath: 'ctcaeTermId',
        TableCellProps: {
            sx: {
                minWidth: 450,
                maxWidth: 450,
                width: 450,
            }
        },
        TableCellContentProps: {
            sx: {
                display:"flex",
                alignItems: "center"
            }
        },
        content: (
            <Input
                component={CtcaeSelector}
                gradeFieldName="ctcaeGradeId"
                specificationFieldName="ctcaeTermSpecify"
                categoryFieldName="ctcaeCategoryId"
                type={CtcaeSelectorType.SelectorWithName}
                ctcaeUrl="https://ctcae.breastcancertrials.org.au/"
            />
        ),
        reserveFlagSpace: false
    },
    {
        propertyPath: 'onsetDate',
        TableCellProps: {
            sx: {
                minWidth: 260,
                maxWidth: 260,
                width: 260,
            }
        },
        content: (
            // <Input
            //     component={Masked}
            //     maskedFormat={UK_DATE}
            // />
            <Input 
                component={DatePicker}
                format={"dd-MMM-yyyy"}
            />
        ),
        reserveFlagSpace: false
    },
    {
        propertyPath: 'resolutionDate',
        TableCellProps: {
            sx: {
                minWidth: 260,
                maxWidth: 260,
                width: 260,
            },
        },
        rowAwareContent: (_parentPropertyPath, index) => (
            // <Input
            //     component={Masked}
            //     maskedFormat={UK_DATE}
            // />
            <CrfCondition
                mode={ConditionMode.Enable}
                condition={resolutionOngoing(index)}
                subscription={{ value: true }}
            >
                <Input 
                    component={DatePicker}
                    format={"dd-MMM-yyyy"}
                />
            </CrfCondition>
        ),
        reserveFlagSpace: false
    },
    {
        propertyPath: 'ongoing',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100,
                justifyContent: 'center',
                pr: 3
            },
        },
        TableCellContentProps: {
            sx: {
                display:"flex",
                alignItems: "center"
            }
        },
        content: (
            <>
                <OngoingUpdater />
                <Input
                    component={Checkbox}
                    label=""
                />
            </>
        ),
        reserveFlagSpace: false
    },
    {
        propertyPath: 'relatedness',
        TableCellProps: {
            sx: {
                minWidth: 260,
                maxWidth: 260,
                width: 260,
            }
        },
        content: (
            <>
                <Input
                    component={Select}
                />
            </>
        ),
        reserveFlagSpace: false
    },
    {
        propertyPath: 'actionTaken',
        TableCellProps: {
            sx: {
                minWidth: 260,
                maxWidth: 260,
                width: 260,
            }
        },
        content: (
            <>
                <Input
                    component={Select}
                />
            </>
        ),
        reserveFlagSpace: false
    },
];

const AdverseEventForm: FC<IAdverseEventFormProps> = ({

}) => {

    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }
    
    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={crfReadOnly(canUpdateCrf)}
                enableQueries
            >
                <FormPatientDetails />
                <Box
                    sx={{
                        pl: 2,
                        pb: 2,
                    }}
                >
                    <Typography 
                        variant="h2"
                    >
                        Adverse Events
                    </Typography>
                </Box>
                <Box
                    sx={{
                        pl: 2,
                        pb: 2,
                    }}
                >
                    <Typography>
                        All Adverse Events are to be reported as per NCI-CTCAE v5.0 from the date main 
                        informed consent is signed until Cycle 3 Day 1, or until T-DXd is permanently ceased. 
                        From Cycle 3 Day 1 onwards, only Adverse Events that result in treatment permanently 
                        ceasing are to be reported.
                    </Typography>
                </Box>
                <GroupedTableArrayField
                    columns={AdverseEventColumns}
                    propertyPath="adverseEventForms"
                    entityName="adverse event"
                />
            </CrfForm>
        </PageLayout>
    )
}

export default AdverseEventForm;